<div class="detail-container">

  <!-- Toolbar -->
  <mat-toolbar color="primary">
    <button mat-icon-button (click)="goBack()" matTooltip="Back to Imaging Cases">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <span style="margin-left: 8px;">
      {{ imagingCase?.label || 'Case Detail' }}
    </span>
    <span class="toolbar-spacer"></span>

    <span *ngIf="imagingCase" class="status-chip" [ngClass]="caseStatusClass(imagingCase.status)">
      {{ imagingCase.status }}
    </span>
  </mat-toolbar>

  <!-- Loading -->
  <div *ngIf="loading" class="center-state">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading case&hellip;</p>
  </div>

  <!-- Not found -->
  <div *ngIf="!loading && !imagingCase" class="center-state">
    <mat-icon class="empty-icon">folder_off</mat-icon>
    <h3>Case not found</h3>
    <button mat-button (click)="goBack()">Go back</button>
  </div>

  <!-- Content -->
  <div *ngIf="!loading && imagingCase" class="content-area">

    <!-- Actions bar -->
    <div class="actions-bar">
      <button mat-raised-button color="primary"
              [disabled]="!canSubmitSelected"
              (click)="submitForProcessing()">
        <mat-spinner *ngIf="submitting" diameter="18" style="display:inline-block;margin-right:6px;"></mat-spinner>
        <mat-icon *ngIf="!submitting">cloud_upload</mat-icon>
        Submit Selected for Processing
      </button>

      <button mat-button
              [disabled]="awaitingUploads.length === 0"
              (click)="selectAllAwaiting()">
        Select all awaiting
      </button>

      <button mat-button
              [disabled]="selectedCount === 0"
              (click)="clearSelection()">
        Clear
      </button>

      <span class="actions-hint" *ngIf="awaitingUploads.length > 0">
        {{ selectedCount }} selected of {{ awaitingUploads.length }} session(s) ready to submit
      </span>

      <span class="spacer"></span>

      <button mat-stroked-button
              [disabled]="!canLink || showLinkPanel"
              [matTooltip]="canLink ? 'Link this case to a Clio matter' : 'Available after processing'"
              (click)="openLinkPanel()">
        <mat-icon>link</mat-icon>
        {{ imagingCase.status === 'linked' ? 'Linked to Clio' : 'Link to Clio Matter' }}
      </button>
    </div>

    <!-- Link-to-Clio inline panel -->
    <div *ngIf="showLinkPanel" class="link-panel">

      <div class="link-panel-header">
        <mat-icon class="link-panel-icon">link</mat-icon>
        <span class="link-panel-title">Select a Clio Matter to Link</span>
        <span class="spacer"></span>
        <button mat-icon-button (click)="cancelLink()" matTooltip="Cancel">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Loading matters -->
      <div *ngIf="mattersLoading" class="link-panel-loading">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Loading matters from Clio&hellip;</span>
      </div>

      <!-- Matters loaded -->
      <ng-container *ngIf="!mattersLoading">
        <mat-form-field class="link-filter-field" appearance="outline">
          <mat-label>Search matters</mat-label>
          <input matInput [(ngModel)]="matterFilter" placeholder="Matter number, name or client…">
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>

        <div class="matters-list" *ngIf="filteredMatters.length > 0; else noMatters">
          <div class="matter-row"
               *ngFor="let m of filteredMatters"
               [class.matter-row-selected]="selectedMatter?.id === m.id"
               (click)="selectMatter(m)">
            <div class="matter-number">{{ m.display_number }}</div>
            <div class="matter-desc">{{ m.description }}</div>
            <div class="matter-client" *ngIf="m.client?.name">{{ m.client.name }}</div>
          </div>
        </div>

        <ng-template #noMatters>
          <p class="muted" style="padding: 12px 0;">
            {{ matterFilter ? 'No matters match your search.' : 'No Clio matters found.' }}
          </p>
        </ng-template>

        <div class="link-panel-footer" *ngIf="selectedMatter">
          <span class="link-selected-label">
            Selected: <strong>{{ selectedMatter.display_number }}</strong>
          </span>
          <button mat-raised-button color="primary"
                  [disabled]="linking"
                  (click)="confirmLink()">
            <mat-spinner *ngIf="linking" diameter="18"
                         style="display:inline-block;margin-right:6px;"></mat-spinner>
            <mat-icon *ngIf="!linking">link</mat-icon>
            Confirm Link
          </button>
        </div>
      </ng-container>
    </div>

    <!-- Upload sessions -->
    <div *ngIf="uploads.length === 0" class="center-state" style="padding: 48px 0;">
      <mat-icon class="empty-icon">inbox</mat-icon>
      <p>No upload sessions found for this case.</p>
    </div>

    <mat-accordion *ngIf="uploads.length > 0" multi="false">
      <mat-expansion-panel *ngFor="let upload of uploads; let i = index" [expanded]="i === 0">

        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-checkbox
              class="upload-select"
              [checked]="isSelected(upload.id)"
              [disabled]="!isAwaiting(upload)"
              (click)="$event.stopPropagation()"
              (change)="toggleSelected(upload.id, $event.checked)">
            </mat-checkbox>
            <span class="status-chip upload-status-chip" [ngClass]="statusClass(displayKey(upload))">
              <mat-icon class="status-chip-icon" [class.spin]="displayKey(upload) === 'processing' || displayKey(upload) === 'scanning'">
                {{ statusIcon(displayKey(upload)) }}
              </mat-icon>
              {{ statusLabel(displayKey(upload)) }}
            </span>
          </mat-panel-title>
          <mat-panel-description>
            {{ upload.fileCount || 0 }} files
            <span *ngIf="upload.failedFiles?.length" class="failed-hint">
              &nbsp;({{ upload.failedFiles.length }} failed)
            </span>
            &nbsp;· Uploaded {{ formatDate(upload.createdAt) }}
          </mat-panel-description>
        </mat-expansion-panel-header>

        <p *ngIf="upload.status === 'quarantined'" class="scan-note scan-bad">
          A virus was found in {{ upload.infectedCount || 1 }} file(s). The infected file(s) were deleted and nothing from this upload was imported.
          Please upload again from a clean source.
        </p>
        <p *ngIf="upload.status === 'error' && upload.scanError" class="scan-note scan-bad">
          This upload could not be scanned, so it was not imported ({{ upload.scanError === 'too_large' ? 'a file is too large to scan' : 'the scanner was unavailable' }}).
        </p>

        <!-- GCS path -->
        <div class="gcs-path">
          <mat-icon class="gcs-icon">storage</mat-icon>
          <span class="gcs-text">{{ upload.gsUriBase }}</span>
        </div>

        <!-- Studies table -->
        <div *ngIf="upload.studies?.length > 0; else noStudies">
          <table class="studies-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Study Date</th>
                <th>Modality</th>
                <th>Description</th>
                <th *ngIf="upload.status === 'imported'">Preview</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let study of upload.studies">
                <td>{{ study.PatientName || '—' }}</td>
                <td>{{ study.StudyDate || '—' }}</td>
                <td>
                  <span class="modality-badge">{{ study.Modality || '—' }}</span>
                </td>
                <td class="desc-cell">{{ study.StudyDescription || '—' }}</td>
                <td *ngIf="upload.status === 'imported'">
                  <a *ngIf="viewerUrl(upload, study.StudyInstanceUID) as url"
                     [href]="url" target="_blank" rel="noopener"
                     mat-icon-button matTooltip="Open in OHIF viewer">
                    <mat-icon>open_in_new</mat-icon>
                  </a>
                  <span *ngIf="!viewerUrl(upload, study.StudyInstanceUID)" class="muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ng-template #noStudies>
          <p class="muted" style="padding: 12px 0;">No study metadata available (DICOMDIR parse may have returned no records).</p>
        </ng-template>

        <!-- Failed files list -->
        <mat-expansion-panel *ngIf="upload.failedFiles?.length" class="failed-files-panel">
          <mat-expansion-panel-header>
            <mat-panel-title class="failed-title">
              <mat-icon style="font-size:16px;height:16px;width:16px;margin-right:4px;">warning</mat-icon>
              {{ upload.failedFiles.length }} file(s) failed to upload
            </mat-panel-title>
          </mat-expansion-panel-header>
          <ul class="failed-list">
            <li *ngFor="let f of upload.failedFiles">{{ f }}</li>
          </ul>
        </mat-expansion-panel>

      </mat-expansion-panel>
    </mat-accordion>

  </div>
</div>
